import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type {SxProps} from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {lighten} from '@mui/system/colorManipulator';
import {DatePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import type {Dayjs} from 'dayjs';
import i18n from 'i18next';
import type {FC} from 'react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import 'dayjs/locale/ca';
import 'dayjs/locale/en';
import {secondaryColor} from '@/theme/theme';

// TYPES
export type Props = {
  editable: boolean;
  value?: Dayjs;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  onChange: (newValue?: Dayjs) => void;
};

const CalendarPickerEditableOnHover: FC<Props> = ({editable, value, maxDate, minDate, onChange}) => {
  const {t} = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  const handleClose = () => {
    setEditing(false);
    setHover(false);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      localeText={{todayButtonLabel: t('forestMass.today')}}
      adapterLocale={lang}
    >
      <Stack
        direction="row"
        spacing={1}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={styles(hover, editable).container}
      >
        <Stack onClick={() => editable && setEditing(true)} style={{cursor: 'pointer', flex: 1}}>
          {!editing && (
            <Typography variant="body1" sx={styles(hover, editable).text}>
              {value?.format('DD/MM/YYYY') ?? '-'}
            </Typography>
          )}
        </Stack>
        {editing && (
          <DatePicker
            disabled={!editable}
            format="DD/MM/YYYY"
            maxDate={maxDate}
            minDate={minDate}
            value={value}
            open={editing}
            onOpen={() => setEditing(true)}
            onClose={handleClose}
            slotProps={{
              popper: {sx: {opacity: 0.95}},
              actionBar: {
                sx: {...styles(hover, editable).actionBar},
              },
              textField: {
                size: 'small',
                variant: 'standard',
                sx: {...styles(hover, editable).textField},
              },
            }}
            onChange={(day: Dayjs | null) => {
              onChange(day == null ? undefined : day);
              handleClose();
            }}
          />
        )}
        <EditOutlinedIcon color="primary" sx={styles(hover, editable).icon} />
      </Stack>
    </LocalizationProvider>
  );
};

export default CalendarPickerEditableOnHover;

const styles = (hover: boolean, editable: boolean): Record<string, SxProps> => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: hover ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: hover ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    pr: 0.5,
  },
  actionBar: {
    justifyContent: 'center',
    '& .MuiButtonBase-root': {
      width: '100px',
      border: 1,
      borderColor: 'primary.main',
      mb: 1,
    },
  },
  textField: {
    '& .MuiInput-underline:before': {display: 'none'},
    '& .MuiInput-underline:after': {display: 'none'},
    '& .MuiInputAdornment-root': {display: 'none'},
  },
  text: {
    pt: 0.5,
    pl: 0.5,
    fontWeight: 900,
    height: '34px',
    color: editable ? 'common.black' : 'grey.500',
  },
  icon: {
    mr: 1,
    opacity: hover ? 1 : 0,
  },
});
